import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { clearError, loginAdmin } from "../../store/slices/authSlice";
import FormInput from "../../components/FormInput/FormInput";
import Button from "../../components/Button/Button";
import ProkopiLogo from "../../assets/icons/prokopi_brand_logo.png";
import "./Login.css";

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error, isAuthenticated } = useSelector((state) => state.auth);

  // Debug logging
  useEffect(() => {
    console.log("🔍 Auth State:", { isLoading, error, isAuthenticated });
  }, [isLoading, error, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  // REMOVED: Don't clear error on mount - only clear when user starts typing
  // useEffect(() => {
  //   dispatch(clearError());
  // }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    if (token && user && !isAuthenticated) {
      try {
        const userData = JSON.parse(user);
        if (userData.role && !["admin", "superadmin"].includes(userData.role)) {
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          localStorage.removeItem("tokenExpiry");
        }
      } catch (err) {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        localStorage.removeItem("tokenExpiry");
      }
    }
  }, [isAuthenticated]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (error) {
      dispatch(clearError());
    }
  };

  const doLogin = () => {
    console.log("🚀 doLogin called");
    if (!formData.email || !formData.password) {
      console.log("❌ Missing email or password");
      return;
    }
    console.log("📤 Dispatching loginAdmin action");
    dispatch(loginAdmin({ email: formData.email, password: formData.password }));
  };

  const handleSubmit = (event) => {
    console.log("📝 Form submitted");
    event.preventDefault();
    event.stopPropagation();
    doLogin();
    return false;
  };

  const fieldError = (field) => {
    if (!error) return null;
    return error.toLowerCase().includes(field) ? error : null;
  };

  const globalError = () => {
    if (!error) return null;
    if (fieldError("email") || fieldError("password")) {
      return null;
    }
    return error;
  };

  return (
    <div className="login-page">
      {/* Left Content Section */}
      <div className="login-content">
        <div className="content-wrapper">
          {/* Logo and Branding */}
          <div className="brand-section">
            <img src={ProkopiLogo} alt="Prokopi" className="brand-logo" />
            <h1 className="brand-title">
              Empowering Your <span className="highlight">Tech Career</span>
            </h1>
            <p className="brand-tagline">
              Join thousands of students transforming their future with industry-ready skills
            </p>
          </div>

          {/* Features Grid */}
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🎓</div>
              <h3>Expert-Led Training</h3>
              <p>Learn from industry professionals with real-world experience</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💼</div>
              <h3>Placement Support</h3>
              <p>95% placement rate with top companies in the industry</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3>Live Projects</h3>
              <p>Build real applications and strengthen your portfolio</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🤝</div>
              <h3>Community Network</h3>
              <p>Join 10,000+ students and alumni in our learning community</p>
            </div>
          </div>

          {/* Student Testimonial */}
          <div className="testimonial-section">
            <div className="student-avatars">
              <div className="avatar">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop" alt="Student" />
              </div>
              <div className="avatar">
                <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop" alt="Student" />
              </div>
              <div className="avatar">
                <img src="https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=100&h=100&fit=crop" alt="Student" />
              </div>
              <div className="avatar">
                <img src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=100&h=100&fit=crop" alt="Student" />
              </div>
              <div className="avatar-count">
                <span>10K+</span>
              </div>
            </div>
            <div className="testimonial-content">
              <p className="testimonial-text">
                "Prokopi transformed my career. The hands-on training and placement support helped me land my dream job at a top tech company!"
              </p>
              <div className="testimonial-author">
                <strong>Priya Sharma</strong>
                <span>Software Engineer @ Google</span>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="stats-bar">
            <div className="stat-item">
              <h4>10,000+</h4>
              <p>Students Trained</p>
            </div>
            <div className="stat-item">
              <h4>500+</h4>
              <p>Hiring Partners</p>
            </div>
            <div className="stat-item">
              <h4>95%</h4>
              <p>Placement Rate</p>
            </div>
            <div className="stat-item">
              <h4>4.9/5</h4>
              <p>Student Rating</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Form Section */}
      <div className="login-form-section">
        {/* Floating decorative elements */}
        <div className="floating-shapes">
          <div className="shape shape-1">📚</div>
          <div className="shape shape-2">💡</div>
          <div className="shape shape-3">🎯</div>
          <div className="shape shape-4">⚡</div>
          <div className="shape shape-5">✨</div>
        </div>

        <div className="form-container">
          <div className="form-header">
            <h2>Admin Portal</h2>
            <p>Sign in to access your dashboard</p>
          </div>

          {/* Error Alert */}
          {globalError() && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              <p>{globalError()}</p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <FormInput
              label="Email Address"
              type="email"
              name="email"
              placeholder="admin@prokopi.in"
              value={formData.email}
              onChange={handleChange}
              error={fieldError("email")}
              required
            />

            <div className="password-field">
              <FormInput
                label="Password"
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                error={fieldError("password")}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="loading-state">
                <div className="spinner"></div>
                <span>Signing you in...</span>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              disabled={isLoading || !formData.email || !formData.password}
              className="submit-btn"
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </Button>

            {/* Footer Link */}
            <div className="form-footer">
              <p>
                Need help? <a href="mailto:support@prokopi.in">Contact Support</a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
